import React from 'react'
import './CompanyDetailModal.css'
import { X, TrendingUp, TrendingDown, DollarSign, Percent, BarChart3, Target, Activity } from 'lucide-react'

const CompanyDetailModal = ({ company, onClose }) => {
  const formatCurrency = (value) => {
    if (!value) return 'N/A'
    if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(2)}B`
    } else if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(2)}K`
    }
    return `$${value.toFixed(2)}`
  }

  const formatPercentage = (value) => {
    if (!value) return 'N/A'
    return `${value.toFixed(2)}%`
  }

  const formatNumber = (value) => {
    if (!value) return 'N/A'
    return value.toFixed(2)
  }

  const getPriceChange = (current, previous) => {
    if (!current || !previous) return null
    const change = current - previous
    const percentChange = (change / previous) * 100
    return { change, percentChange }
  }

  const price1yChange = getPriceChange(company['Price Today'], company['Price 1y ago'])
  const price3yChange = getPriceChange(company['Price Today'], company['Price 3y ago'])
  const price5yChange = getPriceChange(company['Price Today'], company['Price 5y ago'])

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h2>{company.Company}</h2>
            <span className="ticker">{company.Ticker}</span>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="company-overview">
            <div className="price-section">
              <div className="current-price">
                <h3>${company['Price Today']?.toFixed(2) || 'N/A'}</h3>
                <span className="price-label">Current Price</span>
              </div>
              <div className="price-changes">
                {price1yChange && (
                  <div className={`price-change ${price1yChange.change >= 0 ? 'positive' : 'negative'}`}>
                    <span className="change-label">1Y</span>
                    <span className="change-value">
                      {price1yChange.change >= 0 ? '+' : ''}{price1yChange.change.toFixed(2)}
                    </span>
                    <span className="change-percent">
                      ({price1yChange.percentChange >= 0 ? '+' : ''}{price1yChange.percentChange.toFixed(1)}%)
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">
                  <DollarSign size={20} />
                </div>
                <div className="metric-content">
                  <h4>Market Cap</h4>
                  <p>{formatCurrency(company['Market Cap'])}</p>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <BarChart3 size={20} />
                </div>
                <div className="metric-content">
                  <h4>P/E Ratio</h4>
                  <p>{formatNumber(company['P/E'])}</p>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <Percent size={20} />
                </div>
                <div className="metric-content">
                  <h4>Dividend Yield</h4>
                  <p>{formatPercentage(company['Dividend Yield'])}</p>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <Target size={20} />
                </div>
                <div className="metric-content">
                  <h4>ROE</h4>
                  <p>{formatPercentage(company['ROE'])}</p>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <Activity size={20} />
                </div>
                <div className="metric-content">
                  <h4>ROIC</h4>
                  <p>{formatPercentage(company['ROIC'])}</p>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">
                  <BarChart3 size={20} />
                </div>
                <div className="metric-content">
                  <h4>P/B Ratio</h4>
                  <p>{formatNumber(company['P/B'])}</p>
                </div>
              </div>
            </div>

            <div className="company-details">
              <div className="detail-section">
                <h4>Company Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Sector:</span>
                    <span className="detail-value">{company.Sector || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Industry:</span>
                    <span className="detail-value">{company.Industry || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">EPS:</span>
                    <span className="detail-value">{formatNumber(company['EPS'])}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Beta:</span>
                    <span className="detail-value">{formatNumber(company['Beta'])}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Debt to Equity:</span>
                    <span className="detail-value">{formatNumber(company['Debt to Equity'])}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Price History</h4>
                <div className="price-history">
                  <div className="history-item">
                    <span className="history-label">1 Year Ago:</span>
                    <span className="history-value">${company['Price 1y ago']?.toFixed(2) || 'N/A'}</span>
                  </div>
                  <div className="history-item">
                    <span className="history-label">3 Years Ago:</span>
                    <span className="history-value">${company['Price 3y ago']?.toFixed(2) || 'N/A'}</span>
                  </div>
                  <div className="history-item">
                    <span className="history-label">5 Years Ago:</span>
                    <span className="history-value">${company['Price 5y ago']?.toFixed(2) || 'N/A'}</span>
                  </div>
                  <div className="history-item">
                    <span className="history-label">10 Years Ago:</span>
                    <span className="history-value">${company['Price 10y ago']?.toFixed(2) || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>52-Week Range</h4>
                <div className="week-range">
                  <div className="range-item">
                    <span className="range-label">High:</span>
                    <span className="range-value">${company['52W High']?.toFixed(2) || 'N/A'}</span>
                  </div>
                  <div className="range-item">
                    <span className="range-label">Low:</span>
                    <span className="range-value">${company['52W Low']?.toFixed(2) || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CompanyDetailModal 